import React from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { withGoogleMap, GoogleMap, Marker, Polygon, InfoWindow } from 'react-google-maps';
import Geocode from 'react-geocode';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import ThumbUpIcon from '@material-ui/icons/ThumbUpAltOutlined';
import config from '../../config';

Geocode.setApiKey(config.GOOGLE_MAPS_API_KEY);
Geocode.setLanguage('pl');

const areaOptions = {
  strokeColor: "#000",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: "#607D8B",
  fillOpacity: 0.3,
  clickable: false,
  zIndex: 1,
};

const FormMapView = withGoogleMap((props) => {
  return (<GoogleMap
    ref={props.onMapLoad}
    defaultZoom={props.initialLocation ? 12 : 6}
    defaultCenter={props.initialLocation || { lat: 51.919438, lng: 19.145136 }}
    onClick={props.onClick}
    defaultOptions={{ streetViewControl: false, mapTypeControl: true, clickableIcons: false }}
  >
    {props.proposedLocations.map((l, i) => (
      <Marker key={i} position={l} draggable onDragEnd={location => props.onMarkerPositionChanged(i, location)} onClick={() => props.onMarkerInfoWindowToggle(i)}>
        {l.infoWindow ? <InfoWindow onCloseClick={() => props.onMarkerInfoWindowToggle(i)}>
          <div>
            <Typography display="block" color="textPrimary" gutterBottom>{l.address}</Typography>
            <Typography display="block" color="textSecondary" variant="caption" gutterBottom>Chwyć znacznik, aby zmienić jego położenie</Typography>
            <Button size="small" variant="outlined" onClick={() => props.onMarkerDelete(i)}>Usuń punkt</Button>
          </div>
        </InfoWindow> : null}
      </Marker>))}
    {props.existingProposedLocations.map((l, i) => (
      <Marker key={`existing_${i}`} position={l} onClick={() => props.onExistingMarkerInfoWindowToggle(i)} opacity={l.selected ? 1 : 0.6}>
        {l.infoWindow ? <InfoWindow onCloseClick={() => props.onExistingMarkerInfoWindowToggle(i)}>
          <div>
            <Typography display="block" color="textPrimary" gutterBottom>{l.address}</Typography>
            <Button size="small" variant="outlined" color={l.selected ? 'primary' : 'default'} onClick={() => props.onMarkerSlected(i)}><ThumbUpIcon style={{ marginRight: 10 }} />{l.selected ? 'Usuń wybór' : 'Wybierz ten punkt'}</Button>
          </div>
        </InfoWindow> : null}
      </Marker>))}
    {props.areas.map(area => (
      <Polygon
        key={area.id}
        path={area.area.coordinates[0].map(item => ({ lat: item[1], lng: item[0] }))}
        options={{ ...areaOptions }}
      />))
    }
  </GoogleMap>);
});

class FormMapField extends React.Component {
  state = {
    proposedLocations: [],
    existingProposedLocations: this.props.existingProposedLocations,
  };

  validateMarkerPosition = (lat, lng) => {
    const point = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
    };

    return this.props.allowedAreas.some(area => {
      const polygon = { type: 'Feature', geometry: area.area };
      return booleanPointInPolygon(point, polygon);
    });
  }
  
  handleMapClick = location => {
    if (this.state.proposedLocations.length + this.state.existingProposedLocations.filter(l => l.selected).length === this.props.choiceLimits.max) return;

    const lat = location.latLng.lat();
    const lng = location.latLng.lng();
    
    if (this.props.allowedAreas && this.props.allowedAreas.length && !this.validateMarkerPosition(lat, lng)) return;

    Geocode.fromLatLng(lat ,lng).then(
      response => {
        const address = response.results[0].formatted_address.replace('Unnamed Road', 'Nieznana ulica');
        
        const newLocation = {
          lat,
          lng,
          address,
          infoWindow: true,
        };
    
        const newLocations = [...this.state.proposedLocations.map(p => ({ ...p, infoWindow: false })), newLocation];
        const existingProposedLocations = [...this.state.existingProposedLocations.map(p => ({ ...p, infoWindow: false }))];
        this.setState({ proposedLocations: newLocations, existingProposedLocations });
        this.props.input.onChange({ proposed: newLocations, existing: [...this.state.existingProposedLocations.filter(l => l.selected).map(l => l.id)] });
        this.props.input.onBlur();
      },
      error => {
        console.error(error);
      }
    );
  }

  handleMarkerDeleted = index => {
    const newLocations = this.state.proposedLocations.filter((_, i) => i !== index);
    this.setState({ proposedLocations: newLocations });
    this.props.input.onChange({ proposed: newLocations, existing: [...this.state.existingProposedLocations.filter(l => l.selected).map(l => l.id)] });
    this.props.input.onBlur();
  }

  handleMarkerPositionChanged = (index, location) => {
    const lat = location.latLng.lat();
    const lng = location.latLng.lng();

    if (this.props.allowedAreas && this.props.allowedAreas.length && !this.validateMarkerPosition(lat, lng)) {
      this.setState({ proposedLocations: [...this.state.proposedLocations.map(location => Object.assign({}, location))] });
      return;
    }

    Geocode.fromLatLng(lat, lng).then(
      response => {
        const address = response.results[0].formatted_address.replace('Unnamed Road', 'Nieznana ulica');
        
        const updatedLocation = {
          lat,
          lng,
          address,
          infoWindow: true,
        };

        const newLocations = [
          ...this.state.proposedLocations.slice(0, index),
          { ...updatedLocation },
          ...this.state.proposedLocations.slice(index + 1, this.state.proposedLocations.length),
        ];
        this.setState({ proposedLocations: newLocations });
        this.props.input.onChange({ proposed: newLocations, existing: [...this.state.existingProposedLocations.filter(l => l.selected).map(l => l.id)] });
        this.props.input.onBlur();
      },
      error => {
        console.error(error);
      }
    );
  }

  handleInfoWindowToggle = index => {
    const updatedLocation = { ...this.state.proposedLocations[index], infoWindow: !this.state.proposedLocations[index].infoWindow };
    
    const newLocations = [
      ...this.state.proposedLocations.slice(0, index).map(p => ({ ...p, infoWindow: false })),
      { ...updatedLocation },
      ...this.state.proposedLocations.slice(index + 1, this.state.proposedLocations.length).map(p => ({ ...p, infoWindow: false })),
    ];
    const existingProposedLocations = [...this.state.existingProposedLocations.map(p => ({ ...p, infoWindow: false }))];
    this.setState({ proposedLocations: newLocations, existingProposedLocations });
  }

  handleExistingMarkerInfoWindowToggle = index => {
    const updatedLocation = { ...this.state.existingProposedLocations[index], infoWindow: !this.state.existingProposedLocations[index].infoWindow };
    
    const newLocations = [
      ...this.state.existingProposedLocations.slice(0, index).map(p => ({ ...p, infoWindow: false })),
      { ...updatedLocation },
      ...this.state.existingProposedLocations.slice(index + 1, this.state.existingProposedLocations.length).map(p => ({ ...p, infoWindow: false })),
    ];
    const proposedLocations = [...this.state.proposedLocations.map(p => ({ ...p, infoWindow: false }))];
    this.setState({ existingProposedLocations: newLocations, proposedLocations });
  }

  handleExistingLocationSelected = index => {
    if (this.state.proposedLocations.length + this.state.existingProposedLocations.filter(l => l.selected).length === this.props.choiceLimits.max) return;

    const updatedLocation = { ...this.state.existingProposedLocations[index], selected: !this.state.existingProposedLocations[index].selected };
    
    const newLocations = [
      ...this.state.existingProposedLocations.slice(0, index),
      { ...updatedLocation },
      ...this.state.existingProposedLocations.slice(index + 1, this.state.existingProposedLocations.length),
    ];
    this.setState({ existingProposedLocations: newLocations });
    this.props.input.onChange({ proposed: this.state.proposedLocations, existing: [...newLocations.filter(l => l.selected).map(l => l.id)] });
    this.props.input.onBlur();
  }

  render() {
    const { label, meta, allowedAreas, initialLocation } = this.props;
    const { proposedLocations, existingProposedLocations } = this.state;

    return (
      <div style={{ marginTop: 10 }}>
        {label ? <Typography variant="caption">{label}</Typography> : null}
        <FormMapView
          onMapLoad={ref => this.mapRef = ref}
          loadingElement={<div style={{ height: '100%', width: 300 }} />}
          containerElement={<div style={{ width: '100%', height: 400 }} />}
          mapElement={<div style={{ width: '100%', height: 400 }} />}
          onClick={this.handleMapClick}
          areas={allowedAreas || []}
          proposedLocations={proposedLocations}
          onMarkerDelete={this.handleMarkerDeleted}
          onMarkerPositionChanged={this.handleMarkerPositionChanged}
          onMarkerInfoWindowToggle={this.handleInfoWindowToggle}
          onExistingMarkerInfoWindowToggle={this.handleExistingMarkerInfoWindowToggle}
          existingProposedLocations={existingProposedLocations}
          onMarkerSlected={this.handleExistingLocationSelected}
          initialLocation={initialLocation}
        />
        {meta.touched && meta.error ? <Typography variant="caption" color="error" display="block">{meta.error}</Typography> : null}
      </div>
    );
  }
}

FormMapField.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  meta: PropTypes.object.isRequired,
  choiceLimits: PropTypes.object.isRequired,
  allowedAreas: PropTypes.array.isRequired,
  existingLocations: PropTypes.array,
  existingProposedLocations: PropTypes.array.isRequired,
  initialLocation: PropTypes.object,
};

export default FormMapField;



// WEBPACK FOOTER //
// ./src/components/form/FormMapField.js